import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  InputDate,
  InputDecimal,
} from '@/components/_common';
import CustomersSelectField from '@/components/_common/CustomersSelectField';
import {
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Skeleton,
  Tooltip,
  Typography,
} from 'antd';
import { FaCar, FaEdit, FaUserEdit, FaUserPlus } from 'react-icons/fa';
import { KanbanSchedulePortoFormValues } from '../form.types';
import VehicleSelectField from '@/components/_common/VehicleSelectField';
import { FormattedMessage, useIntl } from 'react-intl';
import EmployeesInfiniteSelectField from '@/components/_common/EmployeesInfiniteSelectField';
import PortoPolicyCard from '@/components/_common/PortoPolicyCard';
import { WorkOrderComplementTankLevel } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import moment from 'moment';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import StatusTag from '@/components/_common/StatusTag';
import PortoCPFCustomer from '@/components/Movements/WorkOrders/WorkOrderForm/PortoCPFCustomer';
import PortoVoucherList from '@/components/Movements/WorkOrders/WorkOrderForm/PortoVoucherList';
import { CustomerVehicleReduced } from '@/core/domain/people/CustomerVehicles/CustomerVehicles.types';
import {
  usePortoPoliciesList,
  usePortoPolicieVoucherList,
} from '@/core/domain/porto/policies/porto-policies.hooks';
import { Customer } from '@/core/domain/schedules/Schedules.types';
import { useWorkorderConfigurationCompanyQuery } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';
import { validateCnpj } from '@/utils/functions/validateCnpj';
import validateCpf from '@/utils/functions/validateCpf';
import { useMemo } from 'react';
import { PortoPolicy } from '@/core/domain/porto/policies/porto-policies.types';
import CustomerSimplifiedRegisterModal from '@/components/Customers/CustomerSimplifiedRegisterModal';
import CustomerVehicleSimplifiedRegisterModal from '@/components/Customers/CustomerVehicleSimplifiedRegisterModal';
import useDefaultMinNumberFieldError from '@/hooks/useDefaultMinNumberFieldError';
import VehicleActionButton from '@/components/_common/_VehicleModal/VehicleActionButton';

const { Title } = Typography;

interface Props {
  step: number;
}

export default function KanbanSchedulePortoFormScheduleFields(props: Props) {
  const intl = useIntl();
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const defaultMinNumberFieldError = useDefaultMinNumberFieldError();

  const form = Form.useFormInstance<KanbanSchedulePortoFormValues>();

  const [customerModalVisible, setCustomerModalAction] = useDisclosure();
  const [vehicleModalVisible, setVehicleModalAction] = useDisclosure();
  const vehicle = Form.useWatch(['general', 'vehicle'], form);
  const companyId = Form.useWatch(['general', 'companyId'], form);
  const customerId = Form.useWatch(['general', 'customerId'], form);
  const customerCpfCnpj = Form.useWatch(['general', 'customerCpfCnpj'], form);
  const policyNumber = Form.useWatch(
    ['general', 'policy', 'policyNumber'],
    form,
  );
  const policyBenefits = Form.useWatch(
    ['general', 'policy', 'benefits'],
    form,
  ) as PortoPolicy['benefits'];
  const hasFuelEletric = useMemo(() => {
    return vehicle?.fuelName?.toLowerCase()?.includes('elétrico');
  }, [vehicle]);

  const isValidDocumentCustomer =
    validateCpf(customerCpfCnpj || '') || validateCnpj(customerCpfCnpj || '');

  const workOrderCompanyConfig =
    useWorkorderConfigurationCompanyQuery(companyId);

  const portoPoliciesList = usePortoPoliciesList(
    vehicle?.licensePlate,
    !!vehicle?.licensePlate,
  );

  const hasVoucheInPolicy = useMemo(
    () =>
      !!portoPoliciesList.data
        ?.find((policy) => policy.policyNumber === policyNumber)
        ?.benefits.some((benefit) => benefit.isVoucher),
    [portoPoliciesList.data, policyNumber],
  );

  const portoVouchers = usePortoPolicieVoucherList(
    {
      documentNumber: customerCpfCnpj,
      customerCode: customerId,
    },
    isValidDocumentCustomer &&
      !hasVoucheInPolicy &&
      !portoPoliciesList.isLoading,
  );

  const vouchers = useMemo(
    () => portoVouchers.data?.vouchers || [],
    [portoVouchers.data?.vouchers],
  );

  const handleCustomerModalFinish = (customer: Customer) => {
    form.resetFields(['general', 'customerId']);
    form.setFieldsValue({ general: { customerId: customer.id } });
    setCustomerModalAction.close();
  };

  const handleVehicleModalFinish = async (vehicle: CustomerVehicleReduced) => {
    form.setFieldsValue({
      general: {
        vehicle: {
          id: vehicle.id,
          licensePlate: vehicle.licensePlate,
          fuelName: vehicle.fuel?.name,
        },
      },
    });
  };

  return (
    <div style={{ display: props.step === 0 ? 'block' : 'none' }}>
      <Form.Item
        hidden
        name={['general', 'policy', 'companyCode']}
        rules={
          !!portoPoliciesList.data?.length
            ? [{ required: true, message: 'Selecione uma apólice' }]
            : undefined
        }
      />
      <Form.Item name={['general', 'capsCode']} hidden />
      <Form.Item hidden name={['general', 'policy', 'policyNumber']} />
      <Form.Item hidden name={['general', 'policy', 'customer']} />
      <Form.Item hidden name={['general', 'policy', 'vehicle']} />
      <Form.Item hidden name={['general', 'policy', 'benefits']} />
      <Form.Item hidden name={['general', 'voucher']} />
      <Form.Item hidden name={['general', 'voucher', 'customer']} />
      <Form.Item hidden name={['general', 'voucher', 'address']} />
      <Form.Item hidden name={['general', 'hasVoucher']} initialValue={false} />
      <Form.Item hidden name={['general', 'vehicle', 'licensePlate']} />
      <Form.Item hidden name={['general', 'vehicle', 'fuelName']} />

      <Row gutter={[12, 12]}>
        <Col span={24}>
          <Row gutter={16}>
            <Col span={21}>
              <FloatingFormItem
                name={['general', 'customerId']}
                label="Cliente"
                rules={[defaultEmptyFieldError]}
              >
                <CustomersSelectField
                  showCpfCnpj={true}
                  onClear={() => {
                    form.resetFields(['general', 'vehicle', 'customerId']);
                  }}
                  onCustomerSelected={(customer) => {
                    form.setFieldValue(
                      ['general', 'customerCpfCnpj'],
                      customer?.cpfCnpj,
                    );
                  }}
                />
              </FloatingFormItem>
            </Col>

            <Col span={3} style={{ marginTop: '4px' }}>
              <Tooltip
                title={intl.formatMessage({
                  id: `workOrders.domain.complements.${
                    customerId ? 'customerEdit' : 'customerCreate'
                  }`,
                })}
              >
                <Button
                  size="large"
                  icon={customerId ? <FaUserEdit /> : <FaUserPlus />}
                  onClick={setCustomerModalAction.open}
                />
              </Tooltip>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Row gutter={16}>
            <Col span={21}>
              <FloatingFormItem
                name={['general', 'vehicle', 'id']}
                label={intl.formatMessage({
                  id: 'workOrders.domain.vehicle',
                })}
              >
                <VehicleSelectField
                  customerId={customerId}
                  onVehicleSelect={(vehicle) => {
                    if (vehicle) {
                      form.setFieldsValue({
                        general: {
                          vehicle: {
                            id: vehicle.id,
                            licensePlate: vehicle.licensePlate,
                            fuelName: vehicle.fuel?.name,
                          },
                          customerId: vehicle.customerId,
                        },
                      });
                    }
                  }}
                  queryEnable={!!customerId && !vehicle?.id}
                />
              </FloatingFormItem>
            </Col>

            <Col span={3} style={{ marginTop: '4px' }}>
              <VehicleActionButton
                customerId={customerId}
                vehicleId={vehicle?.id}
                size="large"
                mode={vehicle?.id ? 'edit' : 'create'}
                icon={vehicle?.id ? <FaEdit /> : <FaCar />}
                onClick={setVehicleModalAction.open}
              />
            </Col>
          </Row>
        </Col>

        <Col span={24} md={8}>
          <FloatingFormItem
            label="Empresa"
            name={['general', 'companyId']}
            rules={[defaultEmptyFieldError]}
          >
            <CompaniesInfiniteSelectField
              onCompanySelected={(company) =>
                form.setFieldValue(['general', 'capsCode'], company.capsCode)
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8}>
          <FloatingFormItem
            label="Orçamentista"
            name={['general', 'estimatorId']}
            rules={[defaultEmptyFieldError]}
          >
            <EmployeesInfiniteSelectField
              queryFilter={{ estimator: true, companyId: companyId }}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8}>
          <FloatingFormItem
            label="Duração do serviço em minutos"
            name={['general', 'scheduleDuration']}
            rules={[
              defaultEmptyFieldError,
              defaultMinNumberFieldError(
                1,
                'A duração deve ser maior ou igual a 1 minuto',
              ),
            ]}
          >
            <InputNumber
              style={{
                width: '100%',
              }}
            />
          </FloatingFormItem>
        </Col>

        {(!!customerId || !!vehicle?.licensePlate) && (
          <>
            <Col span={24}>
              <Title level={4}>
                <FormattedMessage id="workOrders.domain.vehicle" />
              </Title>
            </Col>

            <Col span={8}>
              <FloatingFormItem
                name={['general', 'vehicle', 'prisma']}
                label={intl.formatMessage({
                  id: 'workOrders.domain.complements.prisma',
                })}
                rules={[
                  {
                    required: workOrderCompanyConfig.data?.prisma,
                    message: intl.formatMessage({
                      id: 'workOrders.fieldRequiredByConfigCompany',
                    }),
                  },
                ]}
              >
                <Input />
              </FloatingFormItem>
            </Col>

            <Col span={8}>
              <FloatingFormItem
                name={['general', 'vehicle', 'currentKilometers']}
                label={intl.formatMessage({
                  id: 'workOrders.domain.complements.currentKilometers',
                })}
                rules={[
                  {
                    required: workOrderCompanyConfig.data?.isKmRequired,
                    message: intl.formatMessage({
                      id: 'workOrders.fieldRequiredByConfigCompany',
                    }),
                  },
                ]}
              >
                <InputDecimal fractions={0} min={0} max={5000000} />
              </FloatingFormItem>
            </Col>

            <Col span={8}>
              <FloatingFormItem
                name={['general', 'vehicle', 'tankLevel']}
                label={intl.formatMessage({
                  id: 'workOrders.domain.complements.tankLevel',
                })}
                rules={[
                  {
                    required: workOrderCompanyConfig.data?.tankLevel,
                    message: intl.formatMessage({
                      id: 'workOrders.fieldRequiredByConfigCompany',
                    }),
                  },
                ]}
              >
                <Select
                  allowClear
                  style={{ width: '100%' }}
                  options={Object.values(WorkOrderComplementTankLevel).map(
                    (a) => ({
                      label: intl.formatMessage({
                        id: `workOrders.${a}`,
                      }),
                      value: a,
                    }),
                  )}
                />
              </FloatingFormItem>
            </Col>

            <Col span={12}>
              <FloatingFormItem
                name={['general', 'vehicle', 'lastOilChange']}
                label={intl.formatMessage({
                  id: 'workOrders.domain.complements.lastOilChange',
                })}
                rules={hasFuelEletric ? [] : [defaultEmptyFieldError]}
              >
                <InputDate
                  disabledDate={(date) => date.isAfter(moment(), 'day')}
                />
              </FloatingFormItem>
            </Col>

            <Col span={12}>
              <FloatingFormItem
                name={['general', 'vehicle', 'lastOilChangeKilometers']}
                label={intl.formatMessage({
                  id: 'workOrders.domain.complements.lastOilChangeKilometers',
                })}
                rules={hasFuelEletric ? [] : [defaultEmptyFieldError]}
              >
                <InputDecimal fractions={0} min={0} />
              </FloatingFormItem>
            </Col>
          </>
        )}

        {customerId !== 0 && (
          <Col span={24}>
            <div style={{ marginBottom: 24 }}>
              <Title level={4}>
                Apólices{' '}
                {vehicle?.licensePlate ? ` • ${vehicle?.licensePlate}` : ''}
              </Title>

              <Form.Item shouldUpdate noStyle>
                {({ getFieldError }) => {
                  const errors = getFieldError([
                    'general',
                    'policy',
                    'companyCode',
                  ]);
                  return errors.length ? (
                    <span style={{ color: 'red' }}>{errors[0]}</span>
                  ) : null;
                }}
              </Form.Item>
            </div>

            <Skeleton loading={portoPoliciesList.isLoading}>
              <div className="workorder-polices">
                {!portoPoliciesList.data?.length && !vehicle?.licensePlate && (
                  <Card style={{ borderRadius: 8 }}>
                    <Empty description="Informe uma placa para listar as apolices" />
                  </Card>
                )}

                {!portoPoliciesList.data?.length &&
                  vehicle?.licensePlate?.length === 7 && (
                    <Card style={{ borderRadius: 8 }}>
                      <Empty description="Não foi encontrado apolices para a placa informada" />
                    </Card>
                  )}

                {vehicle?.licensePlate?.length === 7 &&
                  portoPoliciesList.data?.map((policy) => (
                    <PortoPolicyCard
                      style={{ marginTop: 16 }}
                      key={policy.policyNumber}
                      active={policy.policyNumber === policyNumber}
                      onClick={() => {
                        form.setFields([
                          { name: ['general', 'customerId'], errors: [] },
                        ]);

                        if (policyNumber === policy.policyNumber) {
                          form.setFieldValue(['general', 'policy'], undefined);
                        } else {
                          const { email, phone, ...restCustomer } =
                            policy.customer;

                          form.setFieldsValue({
                            general: {
                              policy: {
                                companyCode: policy.companyCode,
                                policyNumber: policy.policyNumber,
                                customer: {
                                  ...restCustomer,
                                  contacts: { email, phone },
                                },
                                vehicle: policy.vehicle,
                                benefits: policy.benefits,
                              },
                            },
                          });
                        }
                      }}
                      policy={policy}
                      vehicle={{
                        ...policy.vehicle,
                        licensePlate: vehicle?.licensePlate,
                      }}
                    />
                  ))}
              </div>
            </Skeleton>
          </Col>
        )}

        {customerId === 0 && (
          <Col span={24} md={12}>
            <Title style={{ marginBottom: 24 }} level={4}>
              Dados do cliente por CPF
            </Title>

            <PortoCPFCustomer
              isLoading={portoVouchers.isFetching}
              address={portoVouchers.data?.customer?.address}
              customer={portoVouchers.data?.customer}
            />
          </Col>
        )}

        {!!policyNumber && (
          <Col span={24}>
            <Title level={4} style={{ marginTop: 16 }}>
              Benefícios da apólice
            </Title>

            {!policyNumber && (
              <Empty description="Selecione uma apólice para visualizar os benefícios" />
            )}

            {!!policyNumber && !policyBenefits?.length && (
              <Empty description="Não há benefícios para esta apólice" />
            )}

            {!!policyNumber &&
              policyBenefits?.map((benefit) => (
                <Card
                  style={{ marginBottom: 16, borderRadius: 8 }}
                  bodyStyle={{ padding: 16 }}
                >
                  <strong style={{ display: 'block', fontSize: '1.05rem' }}>
                    {benefit.name}
                  </strong>

                  <Row>
                    <Col span={24} xs={12} sm={8}>
                      <div style={{ fontSize: '0.8rem', color: 'darkgray' }}>
                        Cód. Serviço
                      </div>
                      <div>{benefit.serviceCode}</div>
                    </Col>

                    <Col span={24} xs={12} sm={8}>
                      <div style={{ fontSize: '0.8rem', color: 'darkgray' }}>
                        Última utilização
                      </div>
                      <div>
                        {benefit.wasUsedIn
                          ? moment(benefit.wasUsedIn).format('DD/MM/YYYY')
                          : 'Sem utilização'}
                      </div>
                    </Col>

                    <Col span={24} xs={12} sm={8}>
                      <div style={{ fontSize: '0.8rem', color: 'darkgray' }}>
                        Tipo
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                        }}
                      >
                        {benefit.isVoucher ? (
                          <StatusTag
                            style={{ width: 'fit-content' }}
                            label="Voucher"
                            color="blue"
                          />
                        ) : (
                          <StatusTag
                            style={{ width: 'fit-content' }}
                            label="Benefício"
                            color="green"
                          />
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card>
              ))}
          </Col>
        )}

        {!hasVoucheInPolicy && !!vouchers.length && (
          <Col span={24}>
            <PortoVoucherList
              isLoading={portoVouchers.isLoading}
              vouchers={vouchers || []}
            />
          </Col>
        )}
      </Row>

      <CustomerSimplifiedRegisterModal
        visible={customerModalVisible}
        handleVisible={setCustomerModalAction.close}
        customerId={customerId}
        onFinishCallback={handleCustomerModalFinish}
      />
      <CustomerVehicleSimplifiedRegisterModal
        visible={vehicleModalVisible}
        handleVisible={setVehicleModalAction.toggle}
        customerId={customerId}
        vehicleId={vehicle?.id}
        key={vehicle?.id}
        onFinish={handleVehicleModalFinish}
      />
    </div>
  );
}
